import React, { useCallback, useEffect, useState } from "react";
import { Loader2, MessageCircle, Inbox, RefreshCw, Lock, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import MessageThread from "@/components/messaging/MessageThread";

/**
 * Reusable messaging inbox. Lists /messaging/conversations for the current
 * user and, on click, opens the thread view. Role-agnostic — talent and
 * employer pages mount it with their respective titles.
 */
export const ConversationList = ({ viewerRole, viewerUserId, title, emptyLabel }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/messaging/conversations");
            setRows(data.conversations || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (active) {
        return (
            <MessageThread
                conversation={active}
                viewerUserId={viewerUserId}
                viewerRole={viewerRole}
                onBack={() => { setActive(null); load(); }}
                onConversationChange={(c) => {
                    setActive(c);
                    setRows((prev) => prev.map((p) => (p.id === c.id ? { ...p, ...c } : p)));
                }}
            />
        );
    }

    return (
        <div data-testid="msg-conversation-list">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                        <MessageCircle size={18} /> <p className="overline">Messages</p>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-foreground mt-1">{title}</h1>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="msg-list-refresh"
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center">
                    <Loader2 size={20} className="animate-spin text-emerald-500" />
                </div>
            ) : rows.length === 0 ? (
                <div className="card-soft p-10 text-center" data-testid="msg-list-empty">
                    <Inbox size={28} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-foreground font-medium">{emptyLabel}</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => <ConversationRow key={r.id} row={r} viewerRole={viewerRole} onOpen={setActive} />)}
                </div>
            )}
        </div>
    );
};

const ConversationRow = ({ row, viewerRole, onOpen }) => {
    const statusBadge = (() => {
        if (row.status === "blocked") return { icon: Ban, text: "Blocked", cls: "bg-rose-500/15 text-rose-700 dark:text-rose-400" };
        if (row.status === "closed") return { icon: Lock, text: "Closed", cls: "bg-slate-500/15 text-slate-600 dark:text-slate-400" };
        if (viewerRole === "professional" && !row.talent_accepted_at) {
            return { icon: MessageCircle, text: "New request", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" };
        }
        return { icon: CheckCircle2, text: "Active", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" };
    })();
    const Icon = statusBadge.icon;

    return (
        <button
            type="button"
            onClick={() => onOpen(row)}
            data-testid={`msg-row-${row.id}`}
            className="w-full text-left card-soft p-4 flex items-start gap-3 hover:ring-2 hover:ring-emerald-500/30 transition-shadow"
        >
            <div className="mt-0.5 shrink-0 w-9 h-9 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 flex items-center justify-center">
                <MessageCircle size={16} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-foreground truncate">
                        {viewerRole === "professional"
                            ? `Employer thread`
                            : (row.talent_accepted_at ? "Talent thread" : "Candidate (awaiting reply)")}
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${statusBadge.cls}`}>
                        <Icon size={9} /> {statusBadge.text}
                    </span>
                </div>
                {row.last_message_snippet && (
                    <div className="mt-1 text-xs text-muted-foreground italic truncate">
                        {row.last_message_snippet}
                    </div>
                )}
                {row.last_message_at && (
                    <div className="mt-1 text-[10px] text-muted-foreground">
                        {new Date(row.last_message_at).toLocaleString(undefined, {
                            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                        })}
                    </div>
                )}
            </div>
        </button>
    );
};

export default ConversationList;
